import { useEffect, useMemo, useState } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { toast } from "sonner";
import { apiFetch } from "../../utils/api";
import { Plus, Calendar as CalIcon, ChevronLeft, ChevronRight, Image as ImageIcon, Send, Sparkles, X, Check, ChevronsUpDown, Flame } from "lucide-react";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isSameMonth, parseISO, addMonths, subMonths } from "date-fns";
import { it } from "date-fns/locale";
import { PLATFORM_BY_KEY, PlatformPill, isWeekend } from "./common";
import { MediaPicker } from "./MediaPicker";
import { PostPreview } from "./PostPreview";

// ============================ YouTube Categories ============================
const YT_CATEGORIES = [
  { id: "1", label: "Film & Animation" },
  { id: "2", label: "Autos & Vehicles" },
  { id: "10", label: "Music" },
  { id: "15", label: "Pets & Animals" },
  { id: "17", label: "Sports" },
  { id: "19", label: "Travel & Events" },
  { id: "20", label: "Gaming" },
  { id: "22", label: "People & Blogs" },
  { id: "23", label: "Comedy" },
  { id: "24", label: "Entertainment" },
  { id: "25", label: "News & Politics" },
  { id: "26", label: "Howto & Style" },
  { id: "27", label: "Education" },
  { id: "28", label: "Science & Technology" },
  { id: "29", label: "Nonprofits & Activism" },
];

const POST_TYPE_OPTS = {
  facebook: [{ v: "post", l: "Post" }, { v: "reel", l: "Reel" }, { v: "story", l: "Story" }],
  instagram: [{ v: "post", l: "Post" }, { v: "reel", l: "Reel" }, { v: "story", l: "Story" }, { v: "trial_reel", l: "Trial Reel" }],
  youtube: [{ v: "video", l: "Video" }, { v: "short", l: "Short" }],
};

function defaultPlatformConfig(p) {
  const base = { post_type: POST_TYPE_OPTS[p]?.[0]?.v || null, content: "" };
  if (p === "facebook") return { ...base, title: "" };
  if (p === "instagram") return { ...base, collaborators: "", audio: "", show_reel_on_feed: true, boost_post: false, boost_budget: 0 };
  if (p === "tiktok") return { ...base, post_type: null, privacy: "public", allow_comments: true, allow_duet: true, allow_stitch: true, music: "" };
  if (p === "youtube") return { ...base, title: "", made_for_kids: null, privacy: "public", tags: "", playlist: "", category: "22" };
  return base;
}

function validateForm(form) {
  const errors = { global: [], perPlatform: {} };
  if (!form.client_id) errors.global.push("Seleziona un cliente");
  if (!form.platforms || form.platforms.length === 0) errors.global.push("Seleziona almeno una piattaforma");
  if (!form.media || form.media.length === 0) errors.global.push("Devi allegare almeno un file (foto o video)");
  if (form.content_mode === "single") {
    if (!(form.content_single || "").trim()) errors.global.push("Il testo del post è obbligatorio");
  } else {
    for (const p of form.platforms) {
      const pc = form.platform_config?.[p] || {};
      if (!(pc.content || "").trim()) {
        (errors.perPlatform[p] = errors.perPlatform[p] || []).push("Testo obbligatorio");
      }
    }
  }
  if (form.platforms.includes("youtube")) {
    const yt = form.platform_config?.youtube || {};
    if (!(yt.title || "").trim()) (errors.perPlatform.youtube = errors.perPlatform.youtube || []).push("Title (Video/Short) obbligatorio");
    if (yt.made_for_kids === null || yt.made_for_kids === undefined) (errors.perPlatform.youtube = errors.perPlatform.youtube || []).push("Devi specificare l'Audience configuration (made for kids)");
  }
  return errors;
}

function PresetTypeSelector({ platform, value, onChange }) {
  const opts = POST_TYPE_OPTS[platform] || [];
  if (opts.length === 0) return null;
  return (
    <div>
      <label className="text-xs block mb-1">Tipo di post</label>
      <div className="flex gap-1 flex-wrap">
        {opts.map(o => (
          <button key={o.v} type="button" onClick={() => onChange(o.v)} data-testid={`${platform}-type-${o.v}`}
            className={`px-2.5 py-1 rounded text-xs border transition-colors ${value === o.v ? "bg-slate-900 text-white border-slate-900" : "bg-white hover:bg-slate-50"}`}>{o.l}</button>
        ))}
      </div>
    </div>
  );
}

function NewPostDialog({ open, onOpenChange, selectedDate, clients, accountsByClient, initialClientId, onCreated }) {
  const [form, setForm] = useState({
    client_id: initialClientId || "",
    platforms: [],
    media: [],
    cover: null,
    time: "10:00",
    content_mode: "single",
    content_single: "",
    platform_config: {},
  });
  const [submitting, setSubmitting] = useState(false);
  const [activePresetTab, setActivePresetTab] = useState(null);

  const clientAccounts = accountsByClient[form.client_id] || [];

  const togglePlatform = (p) => {
    setForm(f => {
      const active = f.platforms.includes(p);
      const platforms = active ? f.platforms.filter(x => x !== p) : [...f.platforms, p];
      const platform_config = { ...f.platform_config };
      if (!active) platform_config[p] = platform_config[p] || defaultPlatformConfig(p);
      if (!active && !activePresetTab) setActivePresetTab(p);
      if (active && activePresetTab === p) setActivePresetTab(platforms[0] || null);
      return { ...f, platforms, platform_config };
    });
  };

  const setPC = (p, patch) => setForm(f => ({ ...f, platform_config: { ...f.platform_config, [p]: { ...(f.platform_config[p] || {}), ...patch } } }));

  const errors = useMemo(() => validateForm(form), [form]);
  const hasErrors = errors.global.length > 0 || Object.keys(errors.perPlatform).length > 0;

  const submitPost = async () => {
    if (hasErrors) {
      const first = errors.global[0] || Object.values(errors.perPlatform)[0]?.[0];
      toast.error(first || "Correggi gli errori nel form");
      return;
    }
    setSubmitting(true);
    try {
      const account_ids = [];
      const platforms = [];
      for (const p of form.platforms) {
        const acc = clientAccounts.find(a => a.platform === p);
        if (!acc) { toast.error(`Account ${p} non collegato`); setSubmitting(false); return; }
        account_ids.push(acc.account_id);
        platforms.push(p);
      }
      const [hh, mm] = form.time.split(":");
      const dt = new Date(selectedDate);
      dt.setHours(parseInt(hh, 10), parseInt(mm, 10), 0, 0);
      const r = await apiFetch(`/scheduled-posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: form.client_id,
          content: form.content_single || "",
          platforms,
          account_ids,
          scheduled_for: dt.toISOString(),
          media_urls: (form.media || []).map(m => m.url),
          cover_url: form.cover?.url || null,
          content_mode: form.content_mode,
          platform_config: form.platform_config,
        }),
      });
      if (r.ok) { toast.success("Post programmato"); onCreated(); }
      else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Errore programmazione");
      }
    } finally { setSubmitting(false); }
  };

  const previewContent = (p) => form.content_mode === "single" ? form.content_single : (form.platform_config[p]?.content || "");
  const platformsForPresets = form.platforms.filter(p => ["facebook", "instagram", "tiktok", "youtube"].includes(p));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="cal-post-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-pink-500" />Nuovo post programmato</DialogTitle>
          <DialogDescription>{selectedDate && format(selectedDate, "EEEE d MMMM yyyy", { locale: it })}</DialogDescription>
        </DialogHeader>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5">
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold block mb-1.5 uppercase tracking-wider text-slate-600">1 · Cliente</label>
              <Select value={form.client_id} onValueChange={(v) => setForm(f => ({ ...f, client_id: v, platforms: [], platform_config: {} }))}>
                <SelectTrigger data-testid="cal-client-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {form.client_id && (
              <div>
                <label className="text-xs font-semibold block mb-1.5 uppercase tracking-wider text-slate-600">2 · Piattaforme</label>
                <div className="flex gap-2 flex-wrap">
                  {clientAccounts.map(a => {
                    const meta = PLATFORM_BY_KEY[a.platform];
                    const active = form.platforms.includes(a.platform);
                    return (
                      <button key={a.account_id} onClick={() => togglePlatform(a.platform)} type="button"
                        className={`px-2.5 py-1.5 rounded-md text-xs flex items-center gap-1.5 border transition-colors ${active ? `bg-gradient-to-br ${meta?.gradient} text-white border-transparent` : "bg-white hover:bg-slate-50"}`}
                        data-testid={`cal-platform-${a.platform}`}>
                        {meta?.icon ? <meta.icon className="w-3.5 h-3.5" /> : <span className="text-[10px] font-bold">TT</span>}
                        @{a.username}
                      </button>
                    );
                  })}
                  {clientAccounts.length === 0 && <span className="text-xs text-muted-foreground">Cliente senza account collegati</span>}
                </div>
              </div>
            )}

            {form.platforms.length > 0 && (
              <div>
                <label className="text-xs font-semibold block mb-1.5 uppercase tracking-wider text-slate-600">3 · Testo del post</label>
                <div className="inline-flex rounded-md border p-0.5 mb-2">
                  <button type="button" onClick={() => setForm(f => ({ ...f, content_mode: "single" }))} data-testid="content-mode-single" className={`px-3 py-1 text-xs font-medium rounded ${form.content_mode === "single" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Template unico</button>
                  <button type="button" onClick={() => setForm(f => ({ ...f, content_mode: "per_platform" }))} data-testid="content-mode-per-platform" className={`px-3 py-1 text-xs font-medium rounded ${form.content_mode === "per_platform" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Diverso per social</button>
                </div>
                {form.content_mode === "single" ? (
                  <Textarea value={form.content_single} onChange={(e) => setForm(f => ({ ...f, content_single: e.target.value }))} placeholder="Scrivi qui il testo comune a tutti i social..." rows={3} data-testid="cal-content-single" />
                ) : (
                  <div className="space-y-2">
                    {form.platforms.map(p => (
                      <div key={p}>
                        <div className="flex items-center gap-1.5 mb-1 text-[11px] font-medium text-slate-600 capitalize"><PlatformPill platform={p} /> {p}</div>
                        <Textarea value={form.platform_config[p]?.content || ""} onChange={(e) => setPC(p, { content: e.target.value })} placeholder={`Testo per ${p}...`} rows={2} data-testid={`cal-content-${p}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {form.platforms.length > 0 && (
              <div>
                <label className="text-xs font-semibold block mb-1.5 uppercase tracking-wider text-slate-600">4 · Media (foto o video) — obbligatorio</label>
                <MediaPicker selected={form.media} onChange={(media) => setForm(f => ({ ...f, media }))} />
                {form.media.some(m => m.mime?.startsWith("video/")) && (
                  <div className="border rounded-lg p-3 bg-amber-50 border-amber-200 space-y-2 mt-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-medium flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5 text-amber-600" />Copertina video (Reel)</div>
                        <div className="text-[10px] text-muted-foreground">Per TikTok e IG Reel</div>
                      </div>
                      {form.cover && <button type="button" onClick={() => setForm(f => ({ ...f, cover: null }))} className="text-red-500 hover:text-red-600 text-xs">Rimuovi</button>}
                    </div>
                    {form.cover ? (
                      <div className="flex items-center gap-2">
                        <img src={form.cover.url} alt="cover" className="w-16 h-16 rounded-md object-cover" />
                        <div className="text-xs"><div className="font-medium truncate max-w-[180px]">{form.cover.name}</div></div>
                      </div>
                    ) : (
                      <MediaPicker selected={[]} onChange={(arr) => { const img = arr.find(m => !m.mime?.startsWith("video/")); if (img) setForm(f => ({ ...f, cover: img })); }} />
                    )}
                  </div>
                )}
              </div>
            )}

            {platformsForPresets.length > 0 && (
              <div>
                <label className="text-xs font-semibold block mb-1.5 uppercase tracking-wider text-slate-600">5 · Preset per social</label>
                <Tabs value={activePresetTab || platformsForPresets[0]} onValueChange={setActivePresetTab}>
                  <TabsList className="bg-slate-100 flex-wrap">
                    {platformsForPresets.map(p => {
                      const hasErr = errors.perPlatform[p]?.length > 0;
                      return (
                        <TabsTrigger key={p} value={p} data-testid={`preset-tab-${p}`} className="capitalize">
                          <PlatformPill platform={p} /> {p} {hasErr && <span className="ml-1 text-red-500">!</span>}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                  {platformsForPresets.includes("facebook") && (
                    <TabsContent value="facebook" className="mt-3 space-y-3">
                      <PresetTypeSelector platform="facebook" value={form.platform_config.facebook?.post_type} onChange={(v) => setPC("facebook", { post_type: v })} />
                      <div>
                        <label className="text-xs block mb-1">Title (opzionale)</label>
                        <Input value={form.platform_config.facebook?.title || ""} onChange={(e) => setPC("facebook", { title: e.target.value })} placeholder="Titolo del post FB" data-testid="fb-title" />
                      </div>
                    </TabsContent>
                  )}
                  {platformsForPresets.includes("instagram") && (
                    <TabsContent value="instagram" className="mt-3 space-y-3">
                      <PresetTypeSelector platform="instagram" value={form.platform_config.instagram?.post_type} onChange={(v) => setPC("instagram", { post_type: v })} />
                      <div>
                        <label className="text-xs block mb-1">Collaborators (usernames separati da virgola)</label>
                        <Input value={form.platform_config.instagram?.collaborators || ""} onChange={(e) => setPC("instagram", { collaborators: e.target.value })} placeholder="@user1, @user2" data-testid="ig-collab" />
                      </div>
                      <div>
                        <label className="text-xs block mb-1">Add audio (nome traccia o URL)</label>
                        <Input value={form.platform_config.instagram?.audio || ""} onChange={(e) => setPC("instagram", { audio: e.target.value })} placeholder="Nome audio" data-testid="ig-audio" />
                      </div>
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={form.platform_config.instagram?.show_reel_on_feed || false} onChange={(e) => setPC("instagram", { show_reel_on_feed: e.target.checked })} data-testid="ig-showonfeed" />
                        Show reel on feed
                      </label>
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={form.platform_config.instagram?.boost_post || false} onChange={(e) => setPC("instagram", { boost_post: e.target.checked })} data-testid="ig-boost" />
                        Boost post
                      </label>
                      {form.platform_config.instagram?.boost_post && (
                        <div>
                          <label className="text-xs block mb-1">Budget (€)</label>
                          <Input type="number" min="0" step="1" value={form.platform_config.instagram?.boost_budget || 0} onChange={(e) => setPC("instagram", { boost_budget: parseFloat(e.target.value) || 0 })} className="max-w-[140px]" data-testid="ig-budget" />
                        </div>
                      )}
                    </TabsContent>
                  )}
                  {platformsForPresets.includes("tiktok") && (
                    <TabsContent value="tiktok" className="mt-3 space-y-3">
                      <div>
                        <label className="text-xs block mb-1">Privacy</label>
                        <Select value={form.platform_config.tiktok?.privacy || "public"} onValueChange={(v) => setPC("tiktok", { privacy: v })}>
                          <SelectTrigger data-testid="tt-privacy"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="public">Post public</SelectItem>
                            <SelectItem value="friend">Friend</SelectItem>
                            <SelectItem value="only_me">Only me</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={form.platform_config.tiktok?.allow_comments ?? true} onChange={(e) => setPC("tiktok", { allow_comments: e.target.checked })} data-testid="tt-comments" />
                        Allow comments
                      </label>
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={form.platform_config.tiktok?.allow_duet ?? true} onChange={(e) => setPC("tiktok", { allow_duet: e.target.checked })} data-testid="tt-duet" />
                        Allow duet
                      </label>
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={form.platform_config.tiktok?.allow_stitch ?? true} onChange={(e) => setPC("tiktok", { allow_stitch: e.target.checked })} data-testid="tt-stitch" />
                        Allow stitch
                      </label>
                      <div>
                        <label className="text-xs block mb-1">Music on post (add music)</label>
                        <Input value={form.platform_config.tiktok?.music || ""} onChange={(e) => setPC("tiktok", { music: e.target.value })} placeholder="Aggiungi musica" data-testid="tt-music" />
                      </div>
                    </TabsContent>
                  )}
                  {platformsForPresets.includes("youtube") && (
                    <TabsContent value="youtube" className="mt-3 space-y-3">
                      <PresetTypeSelector platform="youtube" value={form.platform_config.youtube?.post_type} onChange={(v) => setPC("youtube", { post_type: v })} />
                      <div>
                        <label className="text-xs block mb-1">Title (Video/Short) *</label>
                        <Input value={form.platform_config.youtube?.title || ""} onChange={(e) => setPC("youtube", { title: e.target.value })} placeholder="Titolo YouTube" data-testid="yt-title" />
                      </div>
                      <div>
                        <label className="text-xs block mb-1">Audience configuration *</label>
                        <Select value={form.platform_config.youtube?.made_for_kids === null || form.platform_config.youtube?.made_for_kids === undefined ? "" : (form.platform_config.youtube?.made_for_kids ? "yes" : "no")} onValueChange={(v) => setPC("youtube", { made_for_kids: v === "yes" })}>
                          <SelectTrigger data-testid="yt-audience"><SelectValue placeholder="Made for kids?" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Yes, it&apos;s made for kids</SelectItem>
                            <SelectItem value="no">No, it&apos;s not made for kids</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs block mb-1">Privacy</label>
                        <Select value={form.platform_config.youtube?.privacy || "public"} onValueChange={(v) => setPC("youtube", { privacy: v })}>
                          <SelectTrigger data-testid="yt-privacy"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="public">Public</SelectItem>
                            <SelectItem value="unlisted">Unlisted</SelectItem>
                            <SelectItem value="private">Private</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs block mb-1">Tags (separati da virgola)</label>
                        <Input value={form.platform_config.youtube?.tags || ""} onChange={(e) => setPC("youtube", { tags: e.target.value })} placeholder="tag1, tag2, tag3" data-testid="yt-tags" />
                      </div>
                      <div>
                        <label className="text-xs block mb-1">Add to playlist (playlist ID o nome)</label>
                        <Input value={form.platform_config.youtube?.playlist || ""} onChange={(e) => setPC("youtube", { playlist: e.target.value })} placeholder="Playlist" data-testid="yt-playlist" />
                      </div>
                      <div>
                        <label className="text-xs block mb-1">Category</label>
                        <Select value={form.platform_config.youtube?.category || "22"} onValueChange={(v) => setPC("youtube", { category: v })}>
                          <SelectTrigger data-testid="yt-category"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {YT_CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </TabsContent>
                  )}
                </Tabs>
              </div>
            )}

            <div>
              <label className="text-xs font-semibold block mb-1.5 uppercase tracking-wider text-slate-600">6 · Orario di pubblicazione</label>
              <Input type="time" value={form.time} onChange={(e) => setForm(f => ({ ...f, time: e.target.value }))} className="max-w-[140px]" data-testid="cal-time" />
            </div>
          </div>

          <div className="space-y-3">
            {hasErrors && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs space-y-1" data-testid="validation-errors">
                <div className="font-semibold text-red-700 flex items-center gap-1.5"><X className="w-3.5 h-3.5" />Problemi da risolvere</div>
                {errors.global.map((e, i) => <div key={i} className="text-red-600">• {e}</div>)}
                {Object.entries(errors.perPlatform).map(([p, errs]) => errs.map((e, i) => <div key={`${p}-${i}`} className="text-red-600">• <span className="capitalize font-medium">{p}</span>: {e}</div>))}
              </div>
            )}
            {!hasErrors && form.platforms.length > 0 && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs flex items-center gap-1.5 text-emerald-700" data-testid="validation-ok">
                <Check className="w-3.5 h-3.5" />Tutti i controlli superati — pronto per programmare
              </div>
            )}

            <div className="bg-slate-50 rounded-lg p-4">
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Anteprima</div>
              {form.platforms.length === 0 ? (
                <div className="text-xs text-muted-foreground text-center py-12">Seleziona almeno una piattaforma per vedere l&apos;anteprima.</div>
              ) : (
                <div className="space-y-3">
                  {form.platforms.map(p => {
                    const acc = clientAccounts.find(a => a.platform === p);
                    return (
                      <div key={p} className="space-y-1.5">
                        <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-600"><PlatformPill platform={p} /></div>
                        <PostPreview platform={p} content={previewContent(p)} media={form.media} accountInfo={acc} cover={form.cover} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cal-cancel">Annulla</Button>
          <Button onClick={submitPost} disabled={hasErrors || submitting} className="bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50" data-testid="cal-submit">
            <Send className="w-3.5 h-3.5 mr-1" />{submitting ? "Attendere..." : "Programma"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CalendarTab({ clients, accountsByClient, selectedClientId, onClientChange }) {
  const [viewMode, setViewMode] = useState("month");
  const [anchor, setAnchor] = useState(new Date());
  const clientFilter = selectedClientId || "all";
  const setClientFilter = (v) => onClientChange && onClientChange(v === "all" ? "" : v);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [presetClientId, setPresetClientId] = useState("");
  const [showBestTimes, setShowBestTimes] = useState(true);
  const [bestTimesPlatform, setBestTimesPlatform] = useState("all");
  const [bestSlots, setBestSlots] = useState([]);

  const clientsWithAccounts = useMemo(() => clients.filter(c => (accountsByClient[c.client_id] || []).length > 0), [clients, accountsByClient]);

  useEffect(() => {
    if (clientFilter === "all") { setBestSlots([]); return; }
    (async () => {
      try {
        const qs = new URLSearchParams({ client_id: clientFilter });
        if (bestTimesPlatform !== "all") qs.set("platform", bestTimesPlatform);
        const r = await apiFetch(`/zernio/best-times?${qs.toString()}`);
        if (r.ok) { const d = await r.json(); setBestSlots(d.slots || []); }
      } catch { setBestSlots([]); }
    })();
  }, [clientFilter, bestTimesPlatform]);

  const dayIntensity = useMemo(() => {
    const m = {};
    for (const s of bestSlots) {
      const day = s.day_of_week === 0 ? 6 : s.day_of_week - 1;
      const key = day;
      const int = (s.avg_engagement || 0);
      if (!m[key] || int > m[key]) m[key] = int;
    }
    const mx = Object.values(m).reduce((a, b) => Math.max(a, b), 0) || 1;
    const out = {};
    for (const k in m) out[k] = m[k] / mx;
    return out;
  }, [bestSlots]);

  const days = useMemo(() => {
    if (viewMode === "week") {
      const start = startOfWeek(anchor, { weekStartsOn: 1 });
      const end = endOfWeek(anchor, { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    }
    const start = startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [anchor, viewMode]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const start = days[0].toISOString();
      const end = days[days.length - 1].toISOString();
      const qs = new URLSearchParams({ start, end });
      if (clientFilter !== "all") qs.set("client_id", clientFilter);
      const r = await apiFetch(`/scheduled-posts?${qs.toString()}`);
      if (r.ok) setPosts(await r.json());
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchPosts(); }, [anchor, viewMode, clientFilter]); // eslint-disable-line

  const postsByDay = useMemo(() => {
    const map = {};
    for (const p of posts) {
      try {
        const d = parseISO(p.scheduled_for);
        const k = format(d, "yyyy-MM-dd");
        (map[k] = map[k] || []).push(p);
      } catch { /* ignore */ }
    }
    return map;
  }, [posts]);

  const openNewPost = (date) => {
    setSelectedDate(date);
    setPresetClientId(clientFilter !== "all" ? clientFilter : "");
    setShowDialog(true);
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Eliminare questo post programmato?")) return;
    const r = await apiFetch(`/scheduled-posts/${postId}`, { method: "DELETE" });
    if (r.ok) { toast.success("Eliminato"); fetchPosts(); }
  };

  const goPrev = () => setAnchor(viewMode === "week" ? new Date(anchor.getTime() - 7 * 86400000) : subMonths(anchor, 1));
  const goNext = () => setAnchor(viewMode === "week" ? new Date(anchor.getTime() + 7 * 86400000) : addMonths(anchor, 1));
  const labelPeriod = viewMode === "week"
    ? `${format(days[0], "d MMM", { locale: it })} - ${format(days[6], "d MMM yyyy", { locale: it })}`
    : format(anchor, "MMMM yyyy", { locale: it });

  const selectedClientName = clientFilter === "all" ? `Tutti i clienti (${clientsWithAccounts.length})` : (clientsWithAccounts.find(c => c.client_id === clientFilter)?.name || "Seleziona");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="icon" onClick={goPrev} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="text-lg font-semibold tracking-tight capitalize min-w-[220px] text-center" data-testid="cal-period-label">{labelPeriod}</div>
          <Button variant="outline" size="icon" onClick={goNext} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())} data-testid="cal-today">Oggi</Button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-md border p-0.5">
            <button type="button" onClick={() => setViewMode("month")} data-testid="cal-view-month" className={`px-3 py-1 text-xs font-medium rounded ${viewMode === "month" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Mensile</button>
            <button type="button" onClick={() => setViewMode("week")} data-testid="cal-view-week" className={`px-3 py-1 text-xs font-medium rounded ${viewMode === "week" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Settimanale</button>
          </div>
          <button type="button" onClick={() => setShowBestTimes(v => !v)} data-testid="cal-best-times-toggle" className={`px-3 py-1.5 rounded-md text-xs border flex items-center gap-1.5 ${showBestTimes ? "bg-gradient-to-r from-blue-400 to-blue-600 text-white border-transparent" : "bg-white hover:bg-slate-50 text-slate-700"}`}>
            <Flame className="w-3.5 h-3.5" /> Best times
          </button>
          {showBestTimes && clientFilter !== "all" && (
            <Select value={bestTimesPlatform} onValueChange={setBestTimesPlatform}>
              <SelectTrigger className="w-36 h-9" data-testid="cal-best-times-platform"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="facebook">Facebook</SelectItem>
                <SelectItem value="tiktok">TikTok</SelectItem>
                <SelectItem value="youtube">YouTube</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Popover open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="cal-client-filter" className="w-[220px] justify-between">
                <span className="truncate">{selectedClientName}</span>
                <ChevronsUpDown className="w-4 h-4 opacity-50 ml-2" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[240px] p-0" align="end">
              <Command>
                <CommandInput placeholder="Cerca cliente..." data-testid="cal-client-search" />
                <CommandList>
                  <CommandEmpty>Nessun cliente</CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="all" onSelect={() => { setClientFilter("all"); setClientPickerOpen(false); }} data-testid="cal-client-opt-all">
                      <Check className={`mr-2 h-4 w-4 ${clientFilter === "all" ? "opacity-100" : "opacity-0"}`} />
                      Tutti i clienti ({clientsWithAccounts.length})
                    </CommandItem>
                    {clientsWithAccounts.map(c => (
                      <CommandItem key={c.client_id} value={`${c.name} ${c.client_id}`} onSelect={() => { setClientFilter(c.client_id); setClientPickerOpen(false); }} data-testid={`cal-client-opt-${c.client_id}`}>
                        <Check className={`mr-2 h-4 w-4 ${clientFilter === c.client_id ? "opacity-100" : "opacity-0"}`} />
                        <span className="truncate">{c.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <Button onClick={() => openNewPost(new Date())} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="cal-new-post">
            <Plus className="w-4 h-4 mr-1" /> Nuovo post
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="grid grid-cols-7 bg-slate-50 border-b">
          {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map(d => (
            <div key={d} className="px-2 py-2 text-[11px] font-semibold text-slate-600 uppercase tracking-wider text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d, i) => {
            const k = format(d, "yyyy-MM-dd");
            const dayPosts = postsByDay[k] || [];
            const inScope = viewMode === "week" ? true : isSameMonth(d, anchor);
            const isToday = isSameDay(d, new Date());
            const cellMinH = viewMode === "week" ? "min-h-[240px]" : "min-h-[100px]";
            const dow = (d.getDay() + 6) % 7;
            const intensity = showBestTimes ? (dayIntensity[dow] || 0) : 0;
            const bgStyle = intensity > 0 ? { backgroundColor: `rgba(59, 130, 246, ${0.08 + intensity * 0.35})` } : undefined;
            return (
              <div
                key={i}
                onClick={() => openNewPost(d)}
                style={bgStyle}
                className={`${cellMinH} border-b border-r p-1.5 cursor-pointer hover:brightness-105 transition-all ${!inScope ? "bg-slate-50/50 text-slate-400" : ""} ${isWeekend(d) && !intensity ? "bg-slate-50/30" : ""}`}
                data-testid={`cal-day-${k}`}
              >
                <div className="flex items-center justify-between">
                  <div className={`text-xs font-medium ${isToday ? "inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-900 text-white" : ""}`}>{format(d, "d")}</div>
                  {intensity > 0.5 && <Flame className="w-3 h-3 text-blue-600" title={`Best time: engagement medio ${Math.round(intensity * 100)}%`} />}
                </div>
                <div className="space-y-1 mt-1">
                  {dayPosts.slice(0, viewMode === "week" ? 8 : 3).map((p, idx) => (
                    <div key={p.post_id || idx} className="bg-white border rounded px-1.5 py-1 text-[10px] leading-tight hover:border-red-300" onClick={(e) => { e.stopPropagation(); deletePost(p.post_id); }} title="Click per eliminare">
                      <div className="flex gap-0.5 mb-0.5">
                        {p.platforms.map(pl => {
                          const M = PLATFORM_BY_KEY[pl]; const I = M?.icon;
                          return <span key={pl} className={`w-3 h-3 rounded-sm bg-gradient-to-br ${M?.gradient || ''} flex items-center justify-center text-white`}>{I ? <I className="w-2 h-2" /> : <span className="text-[7px] font-bold">TT</span>}</span>;
                        })}
                        <span className="ml-auto text-[9px] text-slate-500">{format(parseISO(p.scheduled_for), "HH:mm")}</span>
                      </div>
                      <div className="truncate font-medium">{p.client_name}</div>
                      <div className="truncate text-slate-500">{p.content?.slice(0, 40)}</div>
                    </div>
                  ))}
                  {dayPosts.length > (viewMode === "week" ? 8 : 3) && <div className="text-[10px] text-slate-500 px-1">+{dayPosts.length - (viewMode === "week" ? 8 : 3)} altri</div>}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      {loading && <div className="text-xs text-muted-foreground">Caricamento post...</div>}

      {showDialog && (
        <NewPostDialog
          open={showDialog}
          onOpenChange={setShowDialog}
          selectedDate={selectedDate}
          clients={clientsWithAccounts}
          accountsByClient={accountsByClient}
          initialClientId={presetClientId}
          onCreated={() => { setShowDialog(false); fetchPosts(); }}
        />
      )}
    </div>
  );
}
